import { COLLECTION_STEPS, type CollectionDto, type CollectionProgramDto, type CollectionStepDto, type CollectionStepKey, type SoftwareDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { navigate } from '../../lib/router';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Badge, Button, Card, ErrorText, PageTitle, Spinner } from '../../ui';
import { DataModelJobs } from './DataModel';
import { MailboxForm } from './Mailboxes';
import { SoftwareCard } from './Software';
import { QueuedUpload, SourceProgress } from './WorkProgress';

const STATE: Record<CollectionStepDto['stato'], { label: string; tone: 'gray' | 'blue' | 'green' | 'amber' }> = {
  da_fare: { label: 'da fare', tone: 'gray' },
  in_corso: { label: 'in lavorazione', tone: 'blue' },
  fatto: { label: 'fatto', tone: 'green' },
  saltato: { label: 'per dopo', tone: 'amber' },
};

const PROGRAM: Record<CollectionProgramDto['stato'], { label: string; tone: 'gray' | 'blue' | 'green' | 'amber' | 'red' }> = {
  da_valutare: { label: 'da valutare', tone: 'gray' },
  collegabile: { label: 'da collegare', tone: 'blue' },
  non_collegabile: { label: 'senza collegamento', tone: 'gray' },
  collegato: { label: 'collegato', tone: 'green' },
  non_usato: { label: 'non usato', tone: 'gray' },
  non_ora: { label: 'per dopo', tone: 'amber' },
};

/**
 * Raccolta dei dati (ADR 0043): finita la configurazione guidata, le fonti di ogni azienda in ordine — posta, fatture
 * dei fornitori, documenti, programmi uno alla volta. Ogni passo lo prepara il sistema su misura per l'azienda; le
 * letture vanno nella coda di lavorazione e la persona può andare avanti mentre il sistema legge.
 */
export function CollectionPage({ step }: { step?: string }) {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const data = useTenantQuery<CollectionDto>(['collection'], '/onboarding/collection', {
    refetchInterval: (d) => (d && (d.lavori.sources > 0 || d.passi.some((p) => p.preparando)) ? 4000 : 30_000),
  });
  const move = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<CollectionDto>(t('/onboarding/collection'), { body }),
    onSuccess: (d) => queryClient.setQueryData(['tenant', tenant.id, 'collection'], d),
  });
  const c = data.data;
  if (!c) return <Card>{data.error ? <ErrorText>{errorMessage(data.error)}</ErrorText> : <Spinner />}</Card>;
  const current = (COLLECTION_STEPS as readonly string[]).includes(step ?? '') ? (step as CollectionStepKey) : (c.consigliato ?? 'posta');
  const s = c.passi.find((p) => p.chiave === current)!;
  const next = c.passi[c.passi.indexOf(s) + 1];
  const go = (k: CollectionStepKey) => navigate(`/t/${tenant.id}/raccolta/${k}`);
  return (
    <>
      <PageTitle
        title="Raccolta dei dati"
        subtitle="Le fonti che ogni azienda ha, in ordine: più il sistema legge, meno ti deve chiedere. Puoi lasciare un passo per dopo e riprenderlo quando vuoi."
      />
      {!c.disponibile && (
        <p className="mb-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
          La configurazione guidata non è ancora finita: puoi cominciare comunque, ma i passi sono pensati per dopo.
        </p>
      )}
      <div className="grid gap-6 md:grid-cols-[16rem_1fr]">
        <nav className="space-y-1">
          {c.passi.map((p) => (
            <button
              key={p.chiave}
              type="button"
              onClick={() => go(p.chiave)}
              className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${p.chiave === current ? 'bg-black/5' : 'hover:bg-black/[0.03]'}`}
            >
              <span className="flex items-center gap-2">
                <span className="font-medium">
                  {p.numero}. {p.titolo}
                </span>
                <span className="flex-1" />
                <span className="shrink-0 whitespace-nowrap">
                  <Badge tone={STATE[p.stato].tone}>{STATE[p.stato].label}</Badge>
                </span>
              </span>
              <span className="mt-0.5 block text-xs text-ink/55">{p.dettaglio}</span>
            </button>
          ))}
        </nav>
        <div className="min-w-0 space-y-4">
          <Card>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-semibold">
                {s.numero}. {s.titolo}
              </h2>
              <Badge tone={STATE[s.stato].tone}>{STATE[s.stato].label}</Badge>
            </div>
            {s.guida ? (
              <p className="mt-2 text-sm text-ink/75">{s.guida.messaggio}</p>
            ) : s.preparando ? (
              <p className="mt-2 flex items-center gap-2 text-sm text-ink/60">
                <Spinner className="h-4 w-4" /> Sto guardando cosa risulta già per preparare questo passo…
              </p>
            ) : null}
            {s.errore && !s.guida && <ErrorText>{s.errore}</ErrorText>}
            {s.preparando && s.guida && <p className="mt-1 text-xs text-ink/45">Aggiorno questo passo con i dati nuovi…</p>}
          </Card>

          {current === 'posta' && <MailStep step={s} />}
          {current === 'fatture' && <InvoicesStep step={s} onDone={() => move.mutate({ passo: 'fatture', azione: 'fatto' })} />}
          {current === 'documenti' && <DocumentsStep step={s} />}
          {current === 'programmi' && <ProgramsStep c={c} onChoice={(programma, scelta) => move.mutate({ passo: 'programmi', programma, scelta })} />}

          {s.fonti.length > 0 && current !== 'programmi' && (
            <Card>
              <h3 className="mb-3 text-sm font-semibold">Letture di questo passo</h3>
              <div className="space-y-2">
                {s.fonti.map((f) => (
                  <SourceProgress key={f.id} source={f} />
                ))}
              </div>
            </Card>
          )}

          <ErrorText>{move.error ? errorMessage(move.error) : ''}</ErrorText>
          <div className="flex flex-wrap items-center gap-2">
            {s.stato === 'saltato' ? (
              <Button variant="ghost" onClick={() => move.mutate({ passo: current, azione: 'riprendi' })}>
                Riprendi questo passo
              </Button>
            ) : s.stato !== 'fatto' ? (
              <Button variant="ghost" onClick={() => move.mutate({ passo: current, azione: 'salta' }, { onSuccess: () => next && go(next.chiave) })}>
                Lascia per dopo
              </Button>
            ) : null}
            <span className="flex-1" />
            {s.stato === 'in_corso' && <span className="text-xs text-ink/55">Puoi andare avanti: la lettura continua da sola.</span>}
            {next ? <Button onClick={() => go(next.chiave)}>Avanti: {next.titolo}</Button> : <Button onClick={() => navigate(`/t/${tenant.id}/dashboard`)}>Vai alla dashboard</Button>}
          </div>
          <DataModelJobs />
        </div>
      </div>
    </>
  );
}

function Proposals({ step, onMailbox }: { step: CollectionStepDto; onMailbox?: (address: string) => void }) {
  const proposals = step.guida?.proposte ?? [];
  if (!proposals.length) return null;
  return (
    <ul className="space-y-2">
      {proposals.map((p, i) => (
        <li key={i} className="rounded-lg border border-black/10 px-3 py-2 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{p.titolo}</span>
            <span className="flex-1" />
            {p.tipo === 'casella' && p.valore && onMailbox && (
              <Button variant="ghost" className="!py-1" onClick={() => onMailbox(p.valore!)}>
                Collega
              </Button>
            )}
          </div>
          <p className="mt-0.5 text-xs text-ink/60">{p.perche}</p>
          <p className="mt-0.5 text-[11px] text-ink/40">Da: {p.fonte}</p>
        </li>
      ))}
    </ul>
  );
}

function MailStep({ step }: { step: CollectionStepDto }) {
  const { tenant } = useTenant();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<string | null>(null);
  return (
    <Card>
      <h3 className="mb-2 text-sm font-semibold">Caselle da collegare</h3>
      <p className="mb-3 text-xs text-ink/55">
        Solo caselle dell’azienda (di un ufficio o di una funzione, come la PEC), mai quelle intestate alle persone. Il sistema conta i messaggi, li legge tutti con la barra di avanzamento e poi legge da solo quelli nuovi.
      </p>
      <Proposals step={step} onMailbox={(a) => setForm(a)} />
      <div className="mt-3">
        {form === null ? (
          <Button variant="ghost" onClick={() => setForm('')}>
            Collega un’altra casella
          </Button>
        ) : (
          <MailboxForm
            key={form}
            initialAddress={form}
            onCancel={() => setForm(null)}
            onDone={() => {
              setForm(null);
              void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
            }}
          />
        )}
      </div>
    </Card>
  );
}

function useSoftware() {
  return useTenantQuery<{ data: SoftwareDto[] }>(['software'], '/software', {
    refetchInterval: (d) => (d?.data.some((s) => s.assessing || s.connection?.syncing || s.exploration?.status === 'in_corso') ? 4000 : false),
  });
}

/** Le schede dei programmi che il sistema propone di collegare in questo passo. */
function ProposedPrograms({ step }: { step: CollectionStepDto }) {
  const sw = useSoftware();
  const keys = (step.guida?.proposte ?? []).filter((p) => p.tipo === 'programma' && p.valore).map((p) => p.valore!);
  const list = (sw.data?.data ?? []).filter((s) => keys.includes(s.key));
  if (!list.length) return null;
  return (
    <div className="space-y-3">
      {list.map((s) => (
        <SoftwareCard key={s.id} s={s} />
      ))}
    </div>
  );
}

function InvoicesStep({ step, onDone }: { step: CollectionStepDto; onDone: () => void }) {
  const suppliers = step.guida?.fornitori ?? [];
  return (
    <>
      <Card>
        <h3 className="mb-3 text-sm font-semibold">Da dove arrivano le fatture</h3>
        <Proposals step={step} />
        <div className="mt-3 space-y-3">
          <ProposedPrograms step={step} />
          <ProgramAsk
            known={(step.guida?.proposte ?? []).some((p) => p.tipo === 'programma' && p.valore)}
            ask="Con quale programma ricevete le fatture dei fornitori?"
            askOther="Usate un altro programma per le fatture?"
            hint="Il gestionale, il programma di fatturazione o il servizio del commercialista: collegandolo arrivano tutte le fatture, anche le nuove, senza caricare file."
            use="Fatture dei fornitori (fatture passive)"
          />
          <p className="pt-1 text-xs font-medium text-ink/60">Oppure carica i file delle fatture</p>
          <QueuedUpload prefix="fatture" label="Carica le fatture dei fornitori: XML, P7M, PDF o lo ZIP scaricato dal cassetto fiscale" />
          {step.stato !== 'fatto' && (
            <Button variant="ghost" onClick={onDone}>
              Arrivano già tutte da una casella collegata
            </Button>
          )}
        </div>
      </Card>
      {suppliers.length > 0 && (
        <Card>
          <h3 className="mb-1 text-sm font-semibold">I fornitori che risultano</h3>
          <p className="mb-3 text-xs text-ink/55">Quelli senza contratto sono i primi da tenere sotto controllo.</p>
          <ul className="divide-y divide-black/5 text-sm">
            {suppliers.map((f, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2 py-2">
                <span className="font-medium">{f.nome}</span>
                <span className="text-xs text-ink/55">{f.cosa}</span>
                <span className="flex-1" />
                <span className="text-xs text-ink/55">{f.documenti} documenti</span>
                <Badge tone={f.contratto === 'trovato' ? 'green' : 'amber'}>{f.contratto === 'trovato' ? 'contratto trovato' : 'senza contratto'}</Badge>
                <span className="w-full text-[11px] text-ink/40">Da: {f.fonte}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </>
  );
}

/**
 * Il programma da cui arrivano i dati di un passo (le fatture dei fornitori, i documenti): ogni azienda ne ha uno. Si
 * indica per nome; il sistema lo valuta (come si collega) e la sua scheda permette di collegarlo.
 */
function ProgramAsk({ known, ask, askOther, hint, use }: { known: boolean; ask: string; askOther: string; hint: string; use: string }) {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const sw = useSoftware();
  const [name, setName] = useState('');
  const [added, setAdded] = useState<string | null>(null);
  const add = useMutation({
    mutationFn: () => api<SoftwareDto>(t('/software'), { body: { name: name.trim(), how: 'dichiarato', description: use } }),
    onSuccess: (s) => {
      setAdded(s.id);
      setName('');
      void queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    },
  });
  const card = added ? sw.data?.data.find((s) => s.id === added) : undefined;
  return (
    <div className="rounded-lg border border-black/10 p-3">
      <p className="text-sm font-medium">{known ? askOther : ask}</p>
      <p className="mt-0.5 text-xs text-ink/55">{hint}</p>
      <form
        className="mt-2 flex flex-wrap gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) add.mutate();
        }}
      >
        <input
          className="min-w-0 flex-1 rounded-lg border border-black/15 px-3 py-1.5 text-sm outline-none focus:border-accent"
          placeholder="Nome del programma"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Button type="submit" disabled={!name.trim() || add.isPending}>
          Aggiungi
        </Button>
      </form>
      <ErrorText>{add.error ? errorMessage(add.error) : ''}</ErrorText>
      {card && (
        <div className="mt-3">
          <SoftwareCard s={card} />
        </div>
      )}
    </div>
  );
}

function DocumentsStep({ step }: { step: CollectionStepDto }) {
  const missing = step.guida?.mancano ?? [];
  return (
    <>
      {missing.length > 0 && (
        <Card>
          <h3 className="mb-1 text-sm font-semibold">Cosa manca ancora</h3>
          <p className="mb-3 text-xs text-ink/55">Non l’ho trovato nella posta né nei file: se ce l’avete in un drive o in una cartella, caricalo.</p>
          <ul className="space-y-2 text-sm">
            {missing.map((m, i) => (
              <li key={i}>
                <span className="font-medium">{m.titolo}</span>
                <span className="block text-xs text-ink/60">{m.perche}</span>
                <span className="block text-[11px] text-ink/40">Da: {m.fonte}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card>
        <h3 className="mb-3 text-sm font-semibold">Dove sono i documenti</h3>
        <Proposals step={step} />
        <div className="mt-3 space-y-3">
          <ProposedPrograms step={step} />
          <ProgramAsk
            known={(step.guida?.proposte ?? []).some((p) => p.tipo === 'programma' && p.valore)}
            ask="Dove tenete i documenti dell’azienda?"
            askOther="Li tenete anche in un’altra piattaforma?"
            hint="Un drive in cloud, un archivio documentale, il gestionale o un portale con i file dentro: collegandolo il sistema legge tutti i documenti, anche quelli nuovi, senza caricarli a mano."
            use="Documenti dell’azienda (drive o archivio documentale)"
          />
          <p className="pt-1 text-xs font-medium text-ink/60">Oppure carica i file</p>
          <QueuedUpload prefix="documenti" label="Carica i documenti dell’azienda: più file insieme o la cartella compressa (ZIP)" />
        </div>
      </Card>
    </>
  );
}

/** I programmi trovati nelle fonti, configurati uno alla volta. */
function ProgramsStep({ c, onChoice }: { c: CollectionDto; onChoice: (programma: string, scelta: 'non_usato' | 'non_ora' | 'riprendi') => void }) {
  const { tenant, t } = useTenant();
  const queryClient = useQueryClient();
  const sw = useSoftware();
  const open = c.programmi.filter((p) => p.stato === 'collegabile' || p.stato === 'da_valutare');
  const [picked, setPicked] = useState<string | null>(null);
  const current = c.programmi.find((p) => p.key === picked) ?? open[0] ?? null;
  const card = current ? sw.data?.data.find((s) => s.key === current.key) : undefined;
  // Un programma non ancora valutato si valuta appena lo si apre (legge la sua documentazione).
  const started = useRef(new Set<string>());
  useEffect(() => {
    if (!card || card.assessment || card.assessing || card.product_unrecognized || started.current.has(card.id)) return;
    started.current.add(card.id);
    void api(t(`/software/${card.id}/assess`), { body: {} }).then(() => queryClient.invalidateQueries({ queryKey: ['tenant', tenant.id] }), () => undefined);
  }, [card]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!c.programmi.length) return null;
  return (
    <>
      {current && (
        <Card>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold">{current.nome}</h3>
            <Badge tone={PROGRAM[current.stato].tone}>{PROGRAM[current.stato].label}</Badge>
          </div>
          {current.da_dove.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-ink/60">
              {current.da_dove.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          )}
          {current.come && <p className="mt-2 text-xs text-ink/60">{current.come}</p>}
          <div className="mt-3">{card ? <SoftwareCard s={card} /> : <Spinner />}</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {current.stato !== 'collegato' && (
              <>
                <Button variant="ghost" onClick={() => onChoice(current.key, 'non_ora')}>
                  Non ora
                </Button>
                <Button variant="ghost" onClick={() => onChoice(current.key, 'non_usato')}>
                  Non lo usiamo più
                </Button>
              </>
            )}
            {current.stato === 'non_ora' && (
              <Button variant="ghost" onClick={() => onChoice(current.key, 'riprendi')}>
                Riprendi
              </Button>
            )}
          </div>
        </Card>
      )}
      <Card>
        <h3 className="mb-2 text-sm font-semibold">Tutti i programmi trovati</h3>
        <ul className="divide-y divide-black/5 text-sm">
          {c.programmi.map((p) => (
            <li key={p.key}>
              <button type="button" onClick={() => setPicked(p.key)} className={`flex w-full flex-wrap items-center gap-2 py-2 text-left ${current?.key === p.key ? 'font-medium' : ''}`}>
                <span>{p.nome}</span>
                <span className="flex-1" />
                {p.sincronizzazione?.status === 'in_corso' && <Badge tone="blue">{p.sincronizzazione.percent}%</Badge>}
                <Badge tone={PROGRAM[p.stato].tone}>{PROGRAM[p.stato].label}</Badge>
              </button>
            </li>
          ))}
        </ul>
      </Card>
    </>
  );
}
